import { message } from 'antd';
import type MapEngine from '@/map-core';
import { AnchorLayerType, LightMapAssistType } from '@/utils/constant';
import {
  returnNormalAnchorFillColor,
  returnSelectedAnchorFillColor,
} from '@/map-core/styles';
import type { EditorMode } from '../store/editorStore';
import { selectIsRoutePicking } from '../store/routePlanStore';
import { DataSyncManager } from './DataSyncManager';
import { SearchManager } from './SearchManager';
import { LaneManager } from './LaneManager';
import { VehicleOverlayManager } from './VehicleOverlayManager';
import { LayerVisibilityManager } from './LayerVisibilityManager';
import { RoutePlanManager } from './RoutePlanManager';
import { mapEditorFetch } from '../utils/fetch';
import { ViewStrategy } from './strategies/ViewStrategy';
import { DrawingStrategy } from './strategies/DrawingStrategy';
import { EditStrategy } from './strategies/EditStrategy';
import type { IControllerBridge, IInteractionStrategy } from './strategies/types';
import type { IStoreBridge } from './storeBridge';
import type { LightMapDraft, LightMapPairFormValues, LightMapWarningFormValues } from '../utils/lightMap';
import { createLightMapPayload } from '../utils/lightMap';

/**
 * 地图编辑器主控制器 — 协调器（Coordinator）
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 策略切换 — 根据编辑器模式（view/drawing/edit+createForm）自动切换交互策略
 * 2. 事件路由 — 接收地图事件（select/draw/moveend），分发到当前策略处理
 * 3. 交互状态 — 管理 curSelectedFeature / overlayRoot / 弹窗 / 高亮 / lane 清理
 * 4. 透传委托 — 将 CRUD / 搜索 / 绘制 / 图层操作委托给子模块或 map-core
 *
 * 不知道 BMapGL 存在，只依赖 MapEngine 门面和 Zustand store
 *
 * 子模块分工：
 * - DataSyncManager:        数据请求（带去重）+ CRUD 操作
 * - SearchManager:          搜索选项懒加载 + 搜索回调
 * - LaneManager:            车道线请求/绘制/清除
 * - VehicleOverlayManager:  车辆框覆盖物绘制/清除
 * - LayerVisibilityManager: 图层显隐统一入口
 * - RoutePlanManager:       路径规划查看（选点/输入坐标/规划请求/独立图层渲染）
 */
export class MapEditorCoordinator implements IControllerBridge {
  map: MapEngine;
  store: IStoreBridge;

  // 子模块（高内聚：每个 Manager 职责单一）
  data: DataSyncManager;
  search: SearchManager;
  lane: LaneManager;
  vehicle: VehicleOverlayManager;
  layerVisibility: LayerVisibilityManager;
  routePlan: RoutePlanManager;

  // 选中 feature
  curSelectedFeature: any = null;

  // 弹窗挂载根节点
  private overlayRoot: HTMLElement | null = null;

  // 策略实例（预创建，切换零开销）
  private viewStrategy = new ViewStrategy();
  private drawingStrategy = new DrawingStrategy();
  private editStrategy = new EditStrategy();
  private strategy: IInteractionStrategy = this.viewStrategy;

  // 订阅清理
  private unsubscribers: Array<() => void> = [];

  // suppressNextMoveEndFetchRef：抑制下一次 moveend 的数据拉取
  private suppressNextMoveEndFetch = false;

  // 选中面导致的 moveend，不应清除选中态
  private isSelectingRef = false;

  // 当前编辑器模式，用于检测从绘制模式切出时兜底清理
  private currentMode: EditorMode = 'view';

  constructor(map: MapEngine, store: IStoreBridge) {
    this.map = map;
    this.store = store;

    // 创建子模块（通过 getMap 懒获取，避免构造顺序问题）
    this.lane = new LaneManager(() => this.map);
    this.data = new DataSyncManager(
      () => this.map,
      () => this.clearPolygonPopups(),
      () => this.resetInteractionState(),
      (item: any) => this.highlightMap(item)
    );
    this.search = new SearchManager();
    this.vehicle = new VehicleOverlayManager(
      () => this.map,
      () => this.vehicle.clearRectangle()
    );
    this.layerVisibility = new LayerVisibilityManager(
      () => this.map,
      (item: any) => this.highlightMap(item)
    );
    this.routePlan = new RoutePlanManager(
      () => this.map,
      store.routePlan,
      mapEditorFetch.getWebRoutePlan
    );

    // 订阅 mode 变化，自动切换策略
    this.unsubscribers.push(
      store.editor.subscribeSession(() => this.syncStrategy())
    );
  }

  // ---- 策略同步 ----

  private syncStrategy() {
    const { mode } = this.store.editor.getSession();
    if (mode === this.currentMode) return;

    // 退出旧策略：调用 onExit生命周期
    this.strategy.onExit?.(this);

    // 从绘制模式切出时，兜底清理地图绘制监听和临时覆盖物
    if (this.currentMode === 'createDrawing' && mode !== 'createDrawing') {
      this.stopDrawing();
    }

    this.currentMode = mode;

    // 切换到新策略
    if (mode === 'createDrawing') {
      this.strategy = this.drawingStrategy;
    } else if (mode === 'edit' || mode === 'createForm') {
      this.strategy = this.editStrategy;
    } else {
      this.strategy = this.viewStrategy;
    }

    // 进入新策略：调用 onEnter 生命周期
    this.strategy.onEnter?.(this);
  }

  // ---- IControllerBridge 实现（供策略调用） ----

  /** 注入 overlayRoot（弹窗挂载点） */
  setOverlayRoot = (root: HTMLElement | null) => {
    this.overlayRoot = root;
  };

  /** 清除地图上的弹窗（state + overlay + DOM 节点） */
  clearPolygonPopups = () => {
    this.store.editor.setPopupContent(null);
    this.map.clearOverlay();
    document.querySelectorAll('.ol-popup').forEach((node) => node.remove());
  };

  /** 关闭详情弹窗：取消多边形选中态并回到浏览模式 */
  closePolygonPopup = () => {
    const { activePolygonData } = this.store.editor.getSession();
    if (activePolygonData?.id) {
      // 再次高亮同一面会触发取消选中并恢复普通态样式，且不触发业务回调
      this.map.highLightPolygon(activePolygonData.id, { panTo: false });
    }
    this.curSelectedFeature = null;
    this.clearPolygonPopups();
    this.store.editor.setBrowseMode();
  };

  /** 重置所有交互态：清除弹窗、删除 lane 线、清除选中 feature */
  resetInteractionState = () => {
    this.clearPolygonPopups();
    this.lane.clear();
    this.curSelectedFeature = null;
  };

  /** 高亮指定多边形（默认触发业务回调，用于手动点击） */
  highlightMap = (item: any, options?: { panTo?: boolean }) => {
    return this.map.highLightPolygon(item?.id, options);
  };

  /**
   * 恢复选中态（用于 moveend / 数据刷新后）。
   * 只应用高亮样式和弹窗，不触发 changeMapCenter，避免地图被拽回中心。
   */
  restoreSelection = (item: any) => {
    const feature = this.map.highLightPolygon(item?.id, { panTo: false });
    if (!feature) return;

    this.handlePolygonPopup(item);
    this.store.editor.setViewMode(item, feature);

    // 限速类型：重新绘制 lane 线
    if (item.type === AnchorLayerType.SPEED_LIMIT) {
      this.lane.drawById(item.id, (feature.coordinates ?? item.coordinates) as any);
    }
  };

  /** 进入编辑模式：清除弹窗并把选中多边形数据写入 session */
  enterEditMode = (data: any) => {
    this.clearPolygonPopups();
    this.store.editor.setEditMode(data);
  };

  /** 创建并显示弹窗 */
  handlePolygonPopup = (data: any) => {
    const { id, coordinates } = data;
    const anchorCoordinates = coordinates[0];
    const currentPopup = this.store.editor.getPopupContent();
    const currentMountNode = currentPopup?.mountNode;

    // 刷新列表后恢复同一选中面时，复用现有弹窗 DOM，只更新数据和锚点位置，避免详情面板闪烁。
    if (currentPopup?.data?.id === id && currentMountNode) {
      this.map.popupManager.updatePopupPosition(id, anchorCoordinates);
      this.store.editor.setPopupContent({
        data,
        mountNode: currentMountNode,
      });
      return;
    }

    this.clearPolygonPopups();
    const { mountNode } = this.map.popupManager.showPopupForData(
      id,
      anchorCoordinates,
      this.overlayRoot || document.body
    );
    this.store.editor.setPopupContent({ data, mountNode });
  };

  delLaneFeature = () => this.lane.clear();
  fetchRecordList = (pos: any, search: any, opts?: any) =>
    this.data.fetchList(pos, search, opts);

  // ---- 稳定 API（传给 React 组件，引用永不变化） ----

  /** 多边形选中/取消选中（由 MapEngine 回调触发） */
  handleSelect = (feature: any, id: number, type: 'focus' | 'blur') => {
    // 路径规划选点阶段：地图点击用于选起终点，屏蔽面选中/详情/平移（PRD 7.3 选点优先）
    // map:click 先于覆盖物 click：选点刚被消费的时间窗内同样屏蔽
    // （点终点落在面上时 stage 已推进到 readyToPlan，谓词拦不住，靠时间窗兜底）
    if (
      selectIsRoutePicking(this.store.routePlan.getSnapshot()) ||
      this.routePlan.wasPickJustConsumed()
    ) {
      return;
    }
    // 标记本次是选中面导致的 moveend
    if (type === 'focus') {
      this.isSelectingRef = true;
    }
    this.strategy.onSelect(this, feature, id, type);
    // 限速区域的 lane 线加载（ViewStrategy 的 onSelect 中无法直接调用 lane）
    if (type === 'focus') {
      const variableList = this.store.editor.getVariableList();
      const data = feature?.data
        ? { ...feature.data, coordinates: feature.coordinates }
        : variableList?.find((item: any) => item.id === id);
      if (data?.type === 'SPEED_LIMIT') {
        // 传入选中多边形坐标（已知 GCJ02）作为参考，自动探测 lane 线坐标系
        this.lane.drawById(data.id, feature?.coordinates ?? data?.coordinates);
      }
      this.curSelectedFeature = feature;
    } else {
      this.curSelectedFeature = null;
    }
  };

  /** 绘制完成回调（由 MapEngine 回调触发） */
  handleDrawComplete = (
    result: boolean,
    feature?: any,
    id?: string | number,
    coordinates?: any
  ) => {
    // 轻图测试绘制：根据 lightMapDraft.stage 分发
    const lightMapDraft = this.store.editor.getSession().lightMapDraft;
    if (lightMapDraft && result) {
      this.handleLightMapDrawComplete(feature, id, coordinates);
      return;
    }
    this.strategy.onDrawComplete(this, result, feature, id, coordinates);
  };

  /** 绘制错误回调（由 MapEngine 回调触发），统一展示 toast */
  handleDrawError = (reason: string) => {
    message.warning(reason);
  };

  /** 用户主动取消绘制（由 MapEngine 回调触发），退出绘制模式 */
  handleDrawCancel = () => {
    // 如果 mode 已被其他逻辑切走（如编辑/浏览），不要把它再覆盖成 browse
    if (this.store.editor.getSession().mode === 'createDrawing') {
      this.resetInteractionState();
      this.store.editor.setBrowseMode();
    }
  };

  /** 仅停止地图绘制监听和临时覆盖物，不触发布局模式切换 */
  stopDrawing = () => {
    this.map.cancelDrawingStatus();
  };

  // ---- 轻图测试绘制流程 ----

  /** 轻图绘制完成分发：根据 stage 决定下一步 */
  private handleLightMapDrawComplete = (
    feature?: any,
    id?: string | number,
    coordinates?: any
  ) => {
    const draft = this.store.editor.getSession().lightMapDraft;
    if (!draft) return;

    const polygon = { id: String(id ?? `lightMap${Date.now()}`), feature, coordinates };

    if (draft.stage === 'bufferDrawing') {
      // 缓冲区绘制完成，进入 bufferReady，并应用缓冲区样式
      this.applyLightMapFeatureStyle(polygon.feature, LightMapAssistType.BUFFER_ZONE);
      this.store.editor.setLightMapDraft({
        ...draft,
        stage: 'bufferReady',
        buffer: polygon,
      });
    } else if (draft.stage === 'parkingDrawing') {
      // 停车区绘制完成，进入 parkingReady，切回 createForm 模式以隐藏右侧蒙层
      this.applyLightMapFeatureStyle(polygon.feature, LightMapAssistType.PARKING_ZONE);
      const nextDraft: LightMapDraft = {
        ...draft,
        stage: 'parkingReady',
        parking: polygon,
      };
      this.store.editor.setCreateFormMode(
        this.store.editor.getSession().draft || {
          id: polygon.id,
          feature: polygon.feature,
          coordinates: polygon.coordinates,
        },
        nextDraft
      );
    } else if (draft.stage === 'warningDrawing') {
      // 危险区绘制完成，进入 warningReady，切回 createForm 模式以隐藏右侧蒙层
      this.applyLightMapFeatureStyle(polygon.feature, LightMapAssistType.WARNING_ZONE);
      const nextDraft: LightMapDraft = {
        ...draft,
        stage: 'warningReady',
        warning: polygon,
      };
      this.store.editor.setCreateFormMode(
        this.store.editor.getSession().draft || {
          id: polygon.id,
          feature: polygon.feature,
          coordinates: polygon.coordinates,
        },
        nextDraft
      );
    }
  };

  /** 根据类型开始创建动态地图（普通类型进入绘制，轻图进入轻图流程） */
  startCreateByType = (type: string) => {
    this.resetInteractionState();
    this.store.editor.setCreateDrawingMode();
    this.map.resetDrawingStyle();
    if (type === 'LD_ASSIST') {
      // 轻图测试需要先选子类型，此处默认进入缓冲区流程
      // 子类型选择在 useSearchTools 的 Modal 中完成
      return;
    }
    this.map.startDraw();
  };

  /** 开始轻图测试创建 */
  startLightMapCreate = (subType: LightMapAssistType) => {
    this.resetInteractionState();
    const stage = subType === LightMapAssistType.WARNING_ZONE
      ? 'warningDrawing'
      : 'bufferDrawing';
    this.store.editor.setCreateDrawingMode();
    this.store.editor.setLightMapDraft({ stage, subType } as LightMapDraft);
    this.setLightMapDrawingStyle(subType);
    this.map.startDraw();
  };

  /** 统一应用轻图测试子类型样式，并同步 feature.data 供后续选中态/普通态样式复用 */
  private applyLightMapFeatureStyle = (feature: any, subType: LightMapAssistType) => {
    feature.setStyle({
      fill: { color: returnNormalAnchorFillColor(AnchorLayerType.LD_ASSIST, subType) },
      stroke: { color: returnSelectedAnchorFillColor(AnchorLayerType.LD_ASSIST, subType), width: 2 },
    });
    feature.data = {
      ...feature.data,
      type: AnchorLayerType.LD_ASSIST,
      subType,
    };
  };

  /** 按轻图测试子类型设置绘制期预览样式 */
  private setLightMapDrawingStyle = (subType: LightMapAssistType) => {
    this.map.setDrawingStyle({
      lineColor: returnSelectedAnchorFillColor(AnchorLayerType.LD_ASSIST, subType),
      polygonStroke: returnSelectedAnchorFillColor(AnchorLayerType.LD_ASSIST, subType),
      polygonFill: returnNormalAnchorFillColor(AnchorLayerType.LD_ASSIST, subType),
      pointFill: returnSelectedAnchorFillColor(AnchorLayerType.LD_ASSIST, subType),
    });
  };

  /** 复用已绘制的多边形作为缓冲区，进入 bufferReady */
  startLightMapBufferFromDraw = () => {
    const session = this.store.editor.getSession();
    const draft = session.draft;
    if (!draft?.feature) return;

    this.applyLightMapFeatureStyle(draft.feature, LightMapAssistType.BUFFER_ZONE);

    const lightMapDraft: LightMapDraft = {
      stage: 'bufferReady',
      subType: LightMapAssistType.BUFFER_ZONE,
      buffer: {
        id: draft.id,
        feature: draft.feature,
      },
    };
    // 隐藏 EditForm，显示 LightMapCreationPanel
    this.store.editor.setLightMapDraft(lightMapDraft);
  };

  /** 应用危险区样式到已绘制的多边形（不设置 lightMapDraft，保持 EditForm 可见） */
  applyLightMapWarningStyle = () => {
    const session = this.store.editor.getSession();
    const draft = session.draft;
    if (!draft?.feature) return;
    this.applyLightMapFeatureStyle(draft.feature, LightMapAssistType.WARNING_ZONE);
  };

  /** 缓冲区完成后，继续绘制停车区 */
  continueLightMapParking = () => {
    const draft = this.store.editor.getSession().lightMapDraft;
    if (!draft || draft.stage !== 'bufferReady') return;
    this.store.editor.setLightMapDraft({ ...draft, stage: 'parkingDrawing' });
    // 切回 createDrawing 模式，隐藏左侧编辑蒙层，恢复地图交互
    this.store.editor.setCreateDrawingMode();
    this.store.editor.setLightMapDraft({ ...draft, stage: 'parkingDrawing' });
    this.setLightMapDrawingStyle(LightMapAssistType.PARKING_ZONE);
    this.map.startDraw();
  };

  /** 重绘轻图某个区域 */
  repaintLightMap = (target: 'buffer' | 'parking' | 'warning') => {
    const draft = this.store.editor.getSession().lightMapDraft;
    if (!draft) return;

    // 必须先切回绘制模式，否则 DrawingStrategy 未激活，startDraw 后地图事件无法进入绘制流程。
    // setCreateDrawingMode 会清空 lightMapDraft，需要在之后重新设置。
    // 右侧蒙层通过 index.tsx 中 `showRightMask = mode === 'createDrawing' && !lightMapDraft`
    // 控制，轻图绘制期间保留 lightMapDraft，因此面板仍可点击。
    this.store.editor.setCreateDrawingMode();

    if (target === 'buffer') {
      // 重绘缓冲区：删除两个草稿，回到 bufferDrawing
      draft.buffer && this.map.deletePolygon(draft.buffer.id);
      draft.parking && this.map.deletePolygon(draft.parking.id);
      this.store.editor.setLightMapDraft({ stage: 'bufferDrawing', subType: draft.subType });
      this.setLightMapDrawingStyle(LightMapAssistType.BUFFER_ZONE);
    } else if (target === 'parking') {
      // 重绘停车区：只删停车区，回到 parkingDrawing
      draft.parking && this.map.deletePolygon(draft.parking.id);
      this.store.editor.setLightMapDraft({ ...draft, stage: 'parkingDrawing', parking: undefined });
      this.setLightMapDrawingStyle(LightMapAssistType.PARKING_ZONE);
    } else if (target === 'warning') {
      // 重绘危险区：删除草稿，回到 warningDrawing
      draft.warning && this.map.deletePolygon(draft.warning.id);
      this.store.editor.setLightMapDraft({ stage: 'warningDrawing', subType: draft.subType });
      this.setLightMapDrawingStyle(LightMapAssistType.WARNING_ZONE);
    }
    this.map.startDraw();
  };

  /** 取消轻图绘制 */
  cancelLightMap = () => {
    const draft = this.store.editor.getSession().lightMapDraft;
    if (draft) {
      draft.buffer && this.map.deletePolygon(draft.buffer.id);
      draft.parking && this.map.deletePolygon(draft.parking.id);
      draft.warning && this.map.deletePolygon(draft.warning.id);
    }
    this.store.editor.clearLightMapDraft();
    this.resetInteractionState();
    this.map.cancelDrawingStatus();
    this.store.editor.setBrowseMode();
  };

  /** 创建轻图测试（调用 DataSyncManager） */
  createLightMap = async (values: LightMapPairFormValues | LightMapWarningFormValues) => {
    const draft = this.store.editor.getSession().lightMapDraft;
    const positionSearchVal = this.store.search.getPositionSearchVal();
    if (!draft || !positionSearchVal.mapId) {
      message.error('轻图测试缺少地图或绘制区域');
      return;
    }

    // 进入 submitting 状态
    this.store.editor.setLightMapDraft({ ...draft, stage: 'submitting' });

    try {
      const payload = createLightMapPayload({
        mapId: Number(positionSearchVal.mapId),
        draft,
        getGeometry: (feature) => this.map.exportGeometry(feature),
        values,
      });
      const success = await this.data.createLightMap(payload);
      if (success) {
        // 清除草稿多边形
        draft.buffer && this.map.deletePolygon(draft.buffer.id);
        draft.parking && this.map.deletePolygon(draft.parking.id);
        draft.warning && this.map.deletePolygon(draft.warning.id);
        this.store.editor.clearLightMapDraft();
        this.store.editor.setBrowseMode();
      } else {
        // 失败回到 ready 状态
        const fallbackStage = draft.subType === LightMapAssistType.WARNING_ZONE
          ? 'warningReady'
          : 'parkingReady';
        this.store.editor.setLightMapDraft({ ...draft, stage: fallbackStage as any });
      }
    } catch (e) {
      message.error('创建轻图测试失败，请重试');
      const fallbackStage = draft.subType === LightMapAssistType.WARNING_ZONE
        ? 'warningReady'
        : 'parkingReady';
      this.store.editor.setLightMapDraft({ ...draft, stage: fallbackStage as any });
    }
  };

  /** 地图 moveend 事件 */
  handleMoveEnd = async () => {
    if (this.suppressNextMoveEndFetch) {
      this.suppressNextMoveEndFetch = false;
      return;
    }
    // 选中面导致的 moveend，不清除选中态
    if (this.isSelectingRef) {
      this.isSelectingRef = false;
      return;
    }
    // 编辑模式下不响应
    const { mode } = this.store.editor.getSession();
    if (mode === 'createDrawing' || mode === 'createForm' || mode === 'edit') {
      return;
    }
    // 更新搜索位置为当前地图中心
    const center = this.map.getCenter();
    if (center) {
      const [lon, lat] = center;
      this.store.search.setPositionSearchVal((prev: any) => ({
        ...prev,
        lon,
        lat,
        longitude: lon,
        latitude: lat,
      }));
    }
    this.strategy.onMoveEnd(this);
  };

  /** 抑制下一次 moveend 数据拉取 */
  suppressNextMoveEnd = () => {
    this.suppressNextMoveEndFetch = true;
  };

  // ---- CRUD 操作（委托给 DataSyncManager） ----

  createPolygon = (values: any) => this.data.create(values);
  editPolygon = (values: any) => this.data.edit(values);
  deletePolygon = (item: any) => this.data.remove(item);
  updateMapStatus = (item: any) => this.data.toggleStatus(item);

  // ---- 搜索操作（委托给 SearchManager） ----

  /**
   * 统一的"搜索并刷新列表"逻辑
   * 复用于：地理位置搜索、站点/停靠点/车辆选择、右侧表单搜索/重置
   */
  searchAndRefresh = (
    position: { lon: number; lat: number },
    rightSearchVal?: any,
    opts?: { resetPage?: boolean }
  ) => {
    this.resetInteractionState();
    this.store.editor.setBrowseMode();
    // 必须在 panTo 前抑制下一次 moveend：BMapGL.panTo 可能同步/半同步触发 moveend，
    // 如果放在 changeSearchPoint 后面，会拦不住程序主动定位带来的列表刷新。
    this.suppressNextMoveEnd();
    this.map.changeSearchPoint([position.lon, position.lat]);
    this.data.fetchList(
      {
        ...this.store.search.getPositionSearchVal(),
        lon: position.lon,
        lat: position.lat,
      },
      rightSearchVal,
      { resetPage: opts?.resetPage ?? true }
    );
    this.store.search.setRightSearchVal(rightSearchVal);
  };

  searchCallBack = (params: any) => this.search.onSearch(params);
  confirmSearch = (params: any) => this.search.onConfirm(params, this);

  // ---- 绘制操作 ----

  startDraw = () => this.map.startDraw();

  cancelDraw = () => {
    const lightMapDraft = this.store.editor.getSession().lightMapDraft;
    if (lightMapDraft) {
      this.cancelLightMap();
      return;
    }
    this.resetInteractionState();
    this.map.cancelDrawingStatus();
    this.store.editor.setBrowseMode();
  };

  repaintDraw = (draft: any) => {
    this.lane.clear();
    this.clearPolygonPopups();
    this.map.deletePolygon(draft.id);
    this.map.startDraw();
  };

  // ---- 图层操作 ----

  changeLayerVisible = (layerId: string, visible: boolean) => {
    this.map.changeLayerVisible(layerId, visible);
  };

  // ---- 车辆框操作 ----

  drawVehicleRectangle = (info: { lon: number; lat: number; heading: number }) =>
    this.vehicle.drawVehicleRectangle(info);

  clearVehicleRectangle = () => this.vehicle.clearRectangle();

  getBasicPosition = (
    stationNum: string,
    updateMapCenter: (pos: { lon: number; lat: number }) => void,
    searchType: string
  ) => this.vehicle.getBasicPosition(stationNum, updateMapCenter, searchType);

  // ---- 清理 ----

  destroy() {
    this.routePlan.destroy();
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
  }
}
